import {
  useCallback, useState, useMemo, useEffect,
} from 'react';
import { pascalCase } from 'change-case';
import {
  getCanonicalPartnerName,
  HOME_DEPOT_NAME,
  PARTNER_LOGO_IMAGE_WIDTH,
} from '../../../../common/constants';
import { useAppSelector } from '../../../../store/hooks';
import { useCustomEventTracking } from '../../../../common/useCustomEventTracking';
import { selectMemberInformation } from '../../../../store/slices/onboardingSlice';
import CompanyProfileDialog from '../Enrollment/CompanyProfileDialog';
import { PartnerEnrollmentDialog } from '../Enrollment/PartnerEnrollmentDialog';
import { usePartnerConfigList, MappedPartnerConfig } from '../Enrollment/usePartnerConfigList';
import { PartnerEnrollmentStatus } from '../../../../common/enums';

export const usePartnerEnrollmentDialog = (partnerId: string) => {
  const [openPartnerModal, setOpenPartnerModal] = useState(false);
  const { company } = useAppSelector(selectMemberInformation);
  const { trackPartnerEnrollment } = useCustomEventTracking();
  const partnerConfigList = usePartnerConfigList();
  const partnerConfig = useMemo(() => partnerConfigList.find(
    (config) => config?.id === partnerId,
  ) as MappedPartnerConfig, [partnerConfigList, partnerId]);
  const [
    shouldHandlePostCompanyProfileSignUp,
    setShouldHandlePostCompanyProfileSignUp,
  ] = useState(false);

  const updatePartnerOpenState = useCallback((newState: boolean) => {
    // Canonical name is derived from the stable code (tier-agnostic), so the
    // Home-Depot check and the analytics event name survive a partner rename.
    const partnerKeyName = getCanonicalPartnerName(
      partnerConfig.partnerCode,
      partnerConfig.partnerName,
    );
    if (!partnerConfig.supportsExistingAccountFlow &&
      partnerKeyName !== HOME_DEPOT_NAME &&
      newState &&
      partnerConfig.enrollment !== PartnerEnrollmentStatus.Completed
    ) {
      trackPartnerEnrollment('Open', `${pascalCase(partnerKeyName)}SignupForm`);
    }
    setOpenPartnerModal(newState);
  }, [setOpenPartnerModal, partnerConfig, trackPartnerEnrollment]);

  useEffect(() => {
    if (shouldHandlePostCompanyProfileSignUp) {
      updatePartnerOpenState(true); // transitions to partner enrollment form
      setShouldHandlePostCompanyProfileSignUp(false);
    }
  }, [shouldHandlePostCompanyProfileSignUp, updatePartnerOpenState]);

  const [showConfirmationState, setShowConfirmationState] = useState(false);

  const PartnerDialog = useMemo(() => (
    company?.profileUpdatedAt ?
      <PartnerEnrollmentDialog
        partnerId={partnerConfig?.id}
        updateEnrollmentDialogOpenState={updatePartnerOpenState}
        updateConfirmationDialogOpenState={setShowConfirmationState}
        confirmationDialogOpenState={showConfirmationState}
        partnerName={partnerConfig?.partnerName}
        partnerCode={partnerConfig?.partnerCode}
        partnerLogoUrl={partnerConfig?.partnerLogoUrl}
        partnerLogoImageWidth={PARTNER_LOGO_IMAGE_WIDTH}
        supportsExistingAccountFlow={!!partnerConfig?.supportsExistingAccountFlow}
        supportsManufacturerFlow={!!partnerConfig?.isManufacturer ||
          !!partnerConfig?.requiresDistributor}
        manufacturerDistributors={partnerConfig?.manufacturerDistributors}
        hasExtraExistingAccQuestion={!!partnerConfig?.hasExtraExistingAccQuestion}
      /> : <CompanyProfileDialog
        updatePartnerOpenState={updatePartnerOpenState}
        optionalPostEnrollmentAction={() => {
          setShouldHandlePostCompanyProfileSignUp(true);
        }} />
  ), [updatePartnerOpenState, company,
    setShowConfirmationState, showConfirmationState, partnerConfig,
  ]);

  return {
    openPartnerModal,
    PartnerDialog,
    updatePartnerOpenState,
  };
};
